import api from '../lib/api';
import { CalendarEvent, CalendarEventDetail, DateWithEvents, CalendarSummary, CalendarFilters, CalendarExportParams } from '../types/calendar';
import { toLocalDateString } from '../utils/dateUtils';
import { formatDateForAPI } from '../utils/calendarUtils';

export interface CalendarEventsResponse {
  success: boolean;
  data: {
    events: CalendarEvent[];
    total: number;
    limit: number;
    offset: number;
  };
}

export interface CalendarEventResponse {
  success: boolean;
  data: CalendarEventDetail;
}

export interface DateWithEventsResponse {
  success: boolean;
  data: DateWithEvents[];
}

export interface CalendarSummaryResponse {
  success: boolean;
  data: CalendarSummary;
}

class CalendarApiService {
  // Get calendar events for a date range
  async getEvents(params: {
    start_date?: string;
    end_date?: string;
    chamber?: string;
    committee_only?: boolean;
    workspace_id?: string;
    my_events?: string;
    sources?: string;
    limit?: number;
    offset?: number;
  }): Promise<CalendarEventsResponse> {
    const response = await api.get('/calendar/events', { params });
    return response.data;
  }

  // Get specific calendar event details
  async getEvent(eventId: number, workspaceId?: string): Promise<CalendarEventResponse> {
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await api.get(`/calendar/events/${eventId}`, { params });
    return response.data;
  }

  // Get dates that have events (for calendar indicators)
  async getDatesWithEvents(params: {
    start_date?: string;
    end_date?: string;
    chamber?: string;
  }): Promise<DateWithEventsResponse> {
    const response = await api.get('/calendar/dates-with-events', { params });
    return response.data;
  }

  // Get calendar summary statistics
  async getSummary(params: {
    start_date?: string;
    end_date?: string;
  }): Promise<CalendarSummaryResponse> {
    const response = await api.get('/calendar/summary', { params });
    return response.data;
  }

  // Helper: get events for a 30-day rolling window starting from anchorDate
  async getEventsFor30Days(anchorDate: Date, filters?: Partial<CalendarFilters>, workspaceId?: string): Promise<CalendarEventsResponse> {
    const endDate = new Date(anchorDate);
    endDate.setDate(anchorDate.getDate() + 29);
    
    return this.getEvents({
      start_date: formatDateForAPI(anchorDate),
      end_date: formatDateForAPI(endDate),
      chamber: filters?.chamber !== 'all' ? filters?.chamber : undefined,
      committee_only: filters?.committee_only,
      workspace_id: workspaceId,
      limit: 1000
    });
  }

  // Helper: get events for a 7-day rolling window starting from anchorDate
  async getEventsFor7Days(anchorDate: Date, filters?: Partial<CalendarFilters>, workspaceId?: string): Promise<CalendarEventsResponse> {
    const endDate = new Date(anchorDate);
    endDate.setDate(anchorDate.getDate() + 6);
    
    return this.getEvents({
      start_date: formatDateForAPI(anchorDate),
      end_date: formatDateForAPI(endDate),
      chamber: filters?.chamber !== 'all' ? filters?.chamber : undefined,
      committee_only: filters?.committee_only,
      workspace_id: workspaceId,
      limit: 1000
    });
  }

  // Helper method to get events for a specific day
  async getEventsForDay(date: Date, filters?: Partial<CalendarFilters>, workspaceId?: string): Promise<CalendarEventsResponse> {
    const dateString = formatDateForAPI(date);
    
    return this.getEvents({
      start_date: dateString,
      end_date: dateString,
      chamber: filters?.chamber !== 'all' ? filters?.chamber : undefined,
      committee_only: filters?.committee_only,
      workspace_id: workspaceId,
      limit: 100
    });
  }

  // Get detailed day view with bills ordered by bill_order
  async getDayDetails(date: string, workspaceId?: string): Promise<{
    success: boolean;
    data: {
      date: string;
      events: Array<CalendarEvent & { bills: Array<{ bill_id: string; bill_order: number }> }>;
      summary: {
        total_bills: number;
        senate_chamber_hearings: number;
        senate_committee_hearings: number;
        house_chamber_hearings: number;
        house_committee_hearings: number;
        total_events: number;
      };
      total_events: number;
      total_bills: number;
    };
  }> {
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await api.get(`/calendar/day/${date}`, { params });
    return response.data;
  }

  // Export calendar events as CSV
  async exportEvents(params: CalendarExportParams): Promise<void> {
    const response = await api.get('/calendar/export', { 
      params,
      responseType: 'blob' // Important for file downloads
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Extract filename from response headers or create default
    const disposition = response.headers['content-disposition'];
    let filename = `calendar-events-${toLocalDateString(new Date())}.csv`;
    
    if (disposition && disposition.includes('filename=')) {
      const filenameMatch = disposition.match(/filename="([^"]+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // Export events for a specific day
  async exportDayEvents(params: {
    date: string;
    workspace_id?: string;
    format?: 'csv';
  }): Promise<void> {
    const response = await api.get(`/calendar/day/${params.date}/export`, { 
      params: {
        workspace_id: params.workspace_id,
        format: params.format || 'csv'
      },
      responseType: 'blob'
    });

    // Handle download (same pattern as existing export)
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    const disposition = response.headers['content-disposition'];
    let filename = `calendar-events-${params.date}.csv`;
    
    if (disposition && disposition.includes('filename=')) {
      const filenameMatch = disposition.match(/filename="([^"]+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // Download single event as .ics file
  async downloadEventICS(eventId: number): Promise<void> {
    const response = await api.get(`/calendar/events/${eventId}/export.ics`, {
      responseType: 'blob'
    });

    // Handle download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    const disposition = response.headers['content-disposition'];
    let filename = `event-${eventId}.ics`;
    
    if (disposition && disposition.includes('filename=')) {
      const filenameMatch = disposition.match(/filename="([^"]+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

export const calendarApi = new CalendarApiService();
